import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Roles } from '../auth/rbac'
import { ENABLE_USER_BILLING } from '../config/featureFlags'
import {
  createMenuVisibilityContext,
  getMenuLabel,
} from '../lib/menu'
import { resolveSidebarUserSections } from '../lib/menu/sidebarMenuLayout'
import { collectSidebarMenuBlocks, findSidebarSectionForPath, getSidebarMainItems } from '../lib/menu/sidebarMenuCollect'
import { buildSidebarSubscriptionInfo } from '../lib/sidebarBillingSummary'
import { api, resolveApiAssetUrl } from '../lib/api'
import MenuIcon from './MenuIcon'
import NavIcon from './NavIcon'
import { MENU_OVERVIEW_PATH } from '../lib/menu/menuOverviewSections'
import { LayoutGrid, ChevronDown, ChevronRight, CreditCard, Search, X } from 'lucide-react'

const SIDEBAR_OPEN_SECTION_KEY = 'sgez.shellSidebar.openSection.v2'
const BUSINESS_INFO_CONFIRM_URL = 'https://www.ftc.go.kr/bizCommPop.do?wrkr_no=5554601166'

function loadOpenSection(fallback) {
  try {
    const raw = window?.localStorage?.getItem(SIDEBAR_OPEN_SECTION_KEY)
    return raw || fallback
  } catch {
    return fallback
  }
}

function saveOpenSection(sectionId) {
  try {
    if (sectionId) window?.localStorage?.setItem(SIDEBAR_OPEN_SECTION_KEY, sectionId)
    else window?.localStorage?.removeItem(SIDEBAR_OPEN_SECTION_KEY)
  } catch {
    /* ignore */
  }
}

function getActiveMenuPath(pathname, paths) {
  if (!pathname) return null
  if (pathname === '/') {
    return (paths || []).includes('/') ? '/' : null
  }

  let best = null
  for (const path of paths || []) {
    if (!path || path === '/') continue
    if (pathname === path || pathname.startsWith(`${path}/`)) {
      if (!best || path.length > best.length) best = path
    }
  }
  return best
}

function isMenuItemActive(pathname, itemPath, menuPaths) {
  if (!itemPath) return false
  return getActiveMenuPath(pathname, menuPaths) === itemPath
}

function sectionHasActiveItem(items, pathname, menuPaths) {
  const activePath = getActiveMenuPath(pathname, menuPaths)
  return (items || []).some((item) => item.path === activePath)
}

function SidebarNavLink({ item, pathname, menuPaths, onNavigate, compact, labelOverride, emphasized = false }) {
  const active = isMenuItemActive(pathname, item.path, menuPaths)
  const label = labelOverride || getMenuLabel(item)
  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={`flex items-center gap-3 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sgez-primary/30 ${
        compact ? 'px-3 py-2.5 text-sm' : 'px-3.5 py-3 text-base'
      } ${
        active
          ? 'border border-sgez-primary/30 border-l-[3px] border-l-sgez-primary bg-blue-50 font-bold text-sgez-primary-dark'
          : emphasized
            ? 'font-bold text-slate-700 hover:bg-sgez-surface-sunken hover:text-slate-950'
            : 'font-semibold text-slate-700 hover:bg-sgez-surface-sunken hover:text-slate-950'
      }`}
    >
      <MenuIcon menuItemId={item.id} emoji={item.icon} className={active ? 'text-sgez-primary-dark' : 'text-slate-500'} size={compact ? 18 : 20} />
      <span className="min-w-0 truncate">{label}</span>
    </Link>
  )
}

function SidebarSection({ sectionId, label, open, active, onToggle, children }) {
  return (
    <div className="border-b border-sgez-border last:border-b-0">
      <button
        type="button"
        className={`mx-2 mb-0.5 flex w-[calc(100%-1rem)] items-center justify-between rounded-lg py-3.5 pl-4 pr-4 text-left text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sgez-primary/30 ${
          active
            ? 'bg-sgez-background font-bold text-sgez-primary-dark hover:bg-sgez-surface-sunken'
            : open
              ? 'bg-sgez-background font-bold text-slate-950 hover:bg-sgez-surface-sunken'
              : 'font-bold text-slate-800 hover:bg-sgez-surface-sunken'
        }`}
        onClick={() => onToggle(sectionId)}
        aria-expanded={open}
        data-active={active ? 'true' : undefined}
      >
        <span className="flex min-w-0 items-center gap-2">
          <NavIcon navItemId={sectionId} size={18} className={active ? 'text-brand-navy' : undefined} />
          <span className="truncate">{label}</span>
        </span>
        {open ? (
          <ChevronDown className={`h-4 w-4 shrink-0 ${active ? 'text-brand-navy' : 'text-slate-600'}`} />
        ) : (
          <ChevronRight className={`h-4 w-4 shrink-0 ${active ? 'text-brand-navy' : 'text-slate-600'}`} />
        )}
      </button>
      {open ? <div className="ml-5 mr-2 space-y-1 border-l border-sgez-border pb-3 pl-2 pt-1">{children}</div> : null}
    </div>
  )
}

function BillingAccessNav({ pathname, onNavigate, footer }) {
  const items = [
    { path: '/billing/subscribe', label: '구독하러가기', Icon: CreditCard, exact: true },
    ENABLE_USER_BILLING ? { path: '/billing', label: '구독 확인', Icon: CreditCard, exact: true } : null,
  ].filter(Boolean)

  return (
    <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-3">
      <div className="space-y-1 px-3">
        {items.map(({ path, label, Icon, exact }) => {
          const active = exact ? pathname === path : (pathname === path || (path !== '/' && pathname.startsWith(`${path}/`)))
          return (
            <Link
              key={path}
              to={path}
              onClick={onNavigate}
              aria-current={active ? 'page' : undefined}
              className={`flex items-center gap-3 rounded-lg px-3.5 py-3 text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sgez-primary/30 ${
                active
                  ? 'border border-sgez-primary/30 border-l-[3px] border-l-sgez-primary bg-blue-50 font-bold text-sgez-primary-dark'
                  : 'font-semibold text-slate-700 hover:bg-sgez-surface-sunken hover:text-slate-950'
              }`}
            >
              <Icon className={active ? 'h-5 w-5 text-sgez-primary-dark' : 'h-5 w-5 text-slate-500'} aria-hidden="true" />
              <span className="min-w-0 truncate">{label}</span>
            </Link>
          )
        })}
      </div>
      {footer ? <div className="mt-[100px] px-3 pb-3">{footer}</div> : null}
    </nav>
  )
}

function resolveProfileAvatarSrc(user, publicPage) {
  const publicUrl = publicPage?.profileImageUrl
  if (publicUrl && String(publicUrl).trim()) return resolveApiAssetUrl(String(publicUrl).trim())
  const publicFileId = publicPage?.profileImageFileId
  if (publicFileId && String(publicFileId).trim()) return resolveApiAssetUrl(String(publicFileId).trim())

  const fileId = user?.profileImageFileId || user?.profile_image_file_id
  if (fileId && String(fileId).trim()) return resolveApiAssetUrl(String(fileId).trim())
  const url = user?.profileImageUrl || user?.profile_image_url
  if (url && String(url).trim()) return resolveApiAssetUrl(String(url).trim())
  return null
}

function SidebarOperatorLabel({ children }) {
  return (
    <div className="border-b border-slate-200/70 bg-slate-100/80 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide text-brand-navy">
      {children}
    </div>
  )
}

function SidebarMenuSearch({ value, onChange }) {
  return (
    <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
      <label className="relative block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="메뉴 검색"
          className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-sky/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
          autoComplete="off"
        />
        {value ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="검색어 지우기"
            onClick={() => onChange('')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </label>
    </div>
  )
}

function SidebarUserHeader({
  user,
  serviceLogoSrc,
  serviceLogoAlt,
  avatarSrc,
  subscriptionInfo,
  onNavigate,
  onLogout,
  mobileOpen,
  onMobileClose,
  billingAccessBlocked = false,
}) {
  const initial = (user?.name || '?').trim().charAt(0).toUpperCase()
  const displayName = user?.name || '사용자'
  const billingPath = ENABLE_USER_BILLING ? '/billing' : '/profile'

  return (
    <>
      <div className="flex h-14 shrink-0 items-center border-b border-slate-200 bg-white px-4">
        <Link to="/" className="flex min-w-0 items-center gap-2.5" onClick={onNavigate}>
          <img src={serviceLogoSrc} alt={serviceLogoAlt} className="h-8 w-8 shrink-0 rounded object-contain" />
          <span className="truncate text-base font-semibold text-slate-900">{serviceLogoAlt}</span>
        </Link>
        {mobileOpen ? (
          <button
            type="button"
            className="ml-auto rounded border border-slate-200 bg-white p-1.5 text-slate-600 hover:bg-slate-50 xl:hidden"
            onClick={onMobileClose}
            aria-label="메뉴 닫기"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-navy text-sm font-bold text-white ring-2 ring-slate-100 shadow-sm">
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="h-full w-full object-cover" />
            ) : (
              initial
            )}
          </div>
          <div className="min-w-0 flex-1 text-left">
            {user?.orgName ? (
              <div className="truncate text-xs font-medium text-slate-600">{user.orgName}</div>
            ) : null}
            <div className="truncate text-sm font-bold text-slate-950">{displayName}</div>
            {subscriptionInfo ? (
              <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-slate-600">
                {subscriptionInfo.badgeLabel ? (
                  <span className="inline-flex shrink-0 items-center rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold leading-none text-emerald-700 ring-1 ring-emerald-200">
                    {subscriptionInfo.badgeLabel}
                  </span>
                ) : null}
                <span className="font-medium text-slate-700">{subscriptionInfo.planLabel}</span>
                <span aria-hidden="true">·</span>
                <span>{subscriptionInfo.detail}</span>
                {subscriptionInfo.showPaymentLink ? (
                  <Link to={billingPath} onClick={onNavigate} className="font-semibold text-brand-navy hover:underline">
                    [결제]
                  </Link>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className={`mt-2.5 grid gap-2 ${billingAccessBlocked ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {!billingAccessBlocked ? (
            <Link
              to="/profile"
              onClick={onNavigate}
              className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold leading-none text-slate-800 hover:bg-slate-50"
            >
              마이페이지
            </Link>
          ) : null}
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold leading-none text-slate-800 hover:bg-slate-50"
          >
            로그아웃
          </button>
        </div>
      </div>
    </>
  )
}

function SidebarNavSections({
  pathname,
  onNavigate,
  mainItems,
  menuBlocks,
  openSectionId,
  onToggleSection,
  menuSearchQuery,
  footer,
}) {
  const normalizedQuery = menuSearchQuery.trim().toLowerCase()

  const allMenuPaths = useMemo(() => {
    const paths = []
    ;(mainItems || []).forEach((item) => { if (item?.path) paths.push(item.path) })
    menuBlocks.forEach((block) => {
      block.items.forEach((item) => {
        if (item?.path) paths.push(item.path)
      })
    })
    return paths
  }, [mainItems, menuBlocks])

  const filteredBlocks = useMemo(() => {
    const mainBlock = mainItems?.length
      ? [{ sectionId: 'main', label: '메인 메뉴', items: mainItems }]
      : []
    const sourceBlocks = [...mainBlock, ...menuBlocks]
    if (!normalizedQuery) return sourceBlocks
    return sourceBlocks
      .map((block) => ({
        ...block,
        items: block.items.filter((item) => getMenuLabel(item).toLowerCase().includes(normalizedQuery)),
      }))
      .filter((block) => block.items.length > 0)
  }, [mainItems, menuBlocks, normalizedQuery])

  if (normalizedQuery) {
    return (
      <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-3">
        {filteredBlocks.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-500">검색 결과가 없습니다</div>
        ) : (
          filteredBlocks.map((block) => (
            <div key={block.sectionId} className="mb-3 px-2">
              <div className="px-2 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">{block.label}</div>
              <div className="space-y-1">
                {block.items.map((item) => (
                  <SidebarNavLink
                    key={item.id}
                    item={item}
                    pathname={pathname}
                    menuPaths={allMenuPaths}
                    onNavigate={onNavigate}
                    compact
                  />
                ))}
              </div>
            </div>
          ))
        )}
        {footer ? <div className="mt-[100px] px-3 pb-3">{footer}</div> : null}
      </nav>
    )
  }

  return (
    <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-2">
      {mainItems?.length ? (
        <div className="mb-2 space-y-1 border-b border-sgez-border px-2 pb-3">
          {mainItems.map((item) => (
            <div key={item.id}>
              <SidebarNavLink
                item={item}
                pathname={pathname}
                menuPaths={allMenuPaths}
                onNavigate={onNavigate}
                labelOverride={item.id === 'personal-task-board' ? '내 업무 바로가기' : undefined}
                emphasized={item.id === 'personal-task-board'}
              />
            </div>
          ))}
        </div>
      ) : null}
      {(() => {
        let subscriptionsLabelShown = false
        let dataLabelShown = false
        let opsLabelShown = false
        return menuBlocks.map((block) => {
          const prefix = []
          if (block.sectionId === 'subscriptions' && !subscriptionsLabelShown) {
            subscriptionsLabelShown = true
            prefix.push(<SidebarOperatorLabel key="label-subscriptions">운영자 · 과금/구독</SidebarOperatorLabel>)
          }
          if (block.sectionId === 'data' && !dataLabelShown) {
            dataLabelShown = true
            prefix.push(<SidebarOperatorLabel key="label-data">운영자 · 데이터</SidebarOperatorLabel>)
          }
          if (block.sectionId.startsWith('ops-') && !opsLabelShown) {
            opsLabelShown = true
            prefix.push(<SidebarOperatorLabel key="label-ops">운영자 · 운영관리</SidebarOperatorLabel>)
          }
          return (
            <React.Fragment key={block.sectionId}>
              {prefix}
              <SidebarSection
                sectionId={block.sectionId}
                label={block.label}
                open={openSectionId === block.sectionId}
                active={sectionHasActiveItem(block.items, pathname, allMenuPaths)}
                onToggle={onToggleSection}
              >
                {block.items.map((item) => (
                  <SidebarNavLink
                    key={item.id}
                    item={item}
                    pathname={pathname}
                    menuPaths={allMenuPaths}
                    onNavigate={onNavigate}
                    compact
                  />
                ))}
              </SidebarSection>
            </React.Fragment>
          )
        })
      })()}
      {footer ? <div className="mt-[100px] px-3 pb-3">{footer}</div> : null}
    </nav>
  )
}

export function SidebarBusinessInfo() {
  return (
    <section aria-label="사업자 정보" className="mt-3 border-t border-sgez-border pt-3 text-[11px] leading-4 text-slate-500">
      <div className="font-semibold text-slate-700">설계EZ (가라멜 스튜디오)</div>
      <div>대표 가승호</div>
      <div>사업자등록번호 555-46-01166</div>
      <div>통신판매업신고번호 2026-부천원미-1316</div>
      <div className="mt-1">경기도 부천시 원미구 수도로 206번길 66-8, 3동 202호</div>
      <div className="mt-1">
        <a className="hover:text-brand-sky" href="tel:05050078200">0505-007-8200</a>
        <span aria-hidden="true"> · </span>
        <a className="hover:text-brand-sky" href="mailto:support@sgez.co.kr">support@sgez.co.kr</a>
      </div>
      <div>호스팅서비스 제공자 Amazon Web Services</div>
      <a
        className="mt-1 inline-flex font-semibold text-slate-600 hover:text-brand-sky"
        href={BUSINESS_INFO_CONFIRM_URL}
        target="_blank"
        rel="noreferrer"
      >
        사업자정보확인
      </a>
    </section>
  )
}
export default function ShellSidebar({
  user,
  hasAnyRole,
  hasAnyPermission,
  hasAnyCapability,
  serviceLogoSrc,
  serviceLogoAlt,
  onLogout,
  mobileOpen,
  onMobileClose,
  billingAccessBlocked = false,
}) {
  const location = useLocation()
  const [menuSearchQuery, setMenuSearchQuery] = useState('')

  const menuCtx = useMemo(
    () => createMenuVisibilityContext(user, { hasAnyRole, hasAnyPermission, hasAnyCapability }),
    [user, hasAnyRole, hasAnyPermission, hasAnyCapability],
  )

  const userSections = useMemo(
    () => resolveSidebarUserSections(menuCtx.isOperator),
    [menuCtx.isOperator],
  )

  const menuBlocks = useMemo(
    () => collectSidebarMenuBlocks(menuCtx, userSections),
    [menuCtx, userSections],
  )

  const mainItems = useMemo(
    () => getSidebarMainItems(menuCtx),
    [menuCtx],
  )

  const defaultOpenSection = useMemo(() => {
    if (location.pathname === '/') return null
    return findSidebarSectionForPath(menuBlocks, location.pathname)
      || menuBlocks.find((b) => b.sectionId === 'customer')?.sectionId
      || menuBlocks[0]?.sectionId
      || null
  }, [menuBlocks, location.pathname])

  const [openSectionId, setOpenSectionId] = useState(() => loadOpenSection(defaultOpenSection))

  useEffect(() => {
    if (location.pathname === '/') {
      setOpenSectionId(null)
      saveOpenSection(null)
      return
    }
    const matched = findSidebarSectionForPath(menuBlocks, location.pathname)
    if (matched) {
      setOpenSectionId(matched)
      saveOpenSection(matched)
    }
  }, [location.pathname, menuBlocks])

  useEffect(() => {
    if (!defaultOpenSection) return
    setOpenSectionId((prev) => prev || defaultOpenSection)
  }, [defaultOpenSection])

  const isOperator = (user?.roles || []).includes(Roles.OPERATOR)
  const billingQuery = useQuery({
    queryKey: ['billing-me-sidebar', user?.id],
    queryFn: async () => {
      const res = await api.get('/billing/me', { __silent: true })
      return res?.data?.data ?? null
    },
    enabled: Boolean(user?.id && !isOperator),
    staleTime: 60_000,
    retry: false,
  })
  const publicPageQuery = useQuery({
    queryKey: ['me', 'public-page', 'sidebar', user?.id],
    queryFn: async () => {
      const res = await api.get('/users/me/public-page', { __silent: true })
      return res?.data?.data ?? res?.data ?? null
    },
    enabled: Boolean(user?.id && (isOperator || billingQuery.data?.billingReady === true)),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const avatarSrc = resolveProfileAvatarSrc(user, publicPageQuery.data)
  const subscriptionInfo = useMemo(
    () => buildSidebarSubscriptionInfo(billingQuery.data),
    [billingQuery.data],
  )

  const onNavigate = () => {
    if (onMobileClose) onMobileClose()
  }

  const toggleSection = (sectionId) => {
    setOpenSectionId((prev) => {
      const next = prev === sectionId ? null : sectionId
      saveOpenSection(next)
      return next
    })
  }

  return (
    <>
      {mobileOpen ? (
        <button type="button" className="fixed inset-0 z-40 bg-black/40 lg:hidden" aria-label="메뉴 닫기" onClick={onMobileClose} />
      ) : null}
      <aside
        id="shell-sidebar"
        className={`fixed inset-y-0 left-0 z-50 flex h-screen w-[17.5rem] flex-col border-r border-sgez-border bg-white transition-transform duration-200 lg:sticky lg:top-0 lg:z-auto lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <SidebarUserHeader
          user={user}
          serviceLogoSrc={serviceLogoSrc}
          serviceLogoAlt={serviceLogoAlt}
          avatarSrc={avatarSrc}
          subscriptionInfo={subscriptionInfo}
          onNavigate={onNavigate}
          onLogout={onLogout}
          mobileOpen={mobileOpen}
          onMobileClose={onMobileClose}
          billingAccessBlocked={billingAccessBlocked}
        />
        {billingAccessBlocked ? (
          <BillingAccessNav
            pathname={location.pathname}
            onNavigate={onNavigate}
            footer={<SidebarBusinessInfo />}
          />
        ) : (
          <>
            <SidebarMenuSearch value={menuSearchQuery} onChange={setMenuSearchQuery} />
            <SidebarNavSections
              pathname={location.pathname}
              onNavigate={onNavigate}
              mainItems={mainItems}
              menuBlocks={menuBlocks}
              openSectionId={openSectionId}
              onToggleSection={toggleSection}
              menuSearchQuery={menuSearchQuery}
              footer={<SidebarBusinessInfo />}
            />
          </>
        )}
        <div className="mt-auto shrink-0 border-t border-sgez-border bg-white p-3">
          <Link
            to={MENU_OVERVIEW_PATH}
            onClick={onNavigate}
            aria-current={location.pathname === MENU_OVERVIEW_PATH ? 'page' : undefined}
            className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sgez-primary/30 ${
              location.pathname === MENU_OVERVIEW_PATH
                ? 'border-sgez-primary/30 bg-blue-50 text-sgez-primary-dark ring-1 ring-sgez-primary/20'
                : 'border-sgez-border bg-white text-slate-800 hover:bg-sgez-surface-sunken'
            }`}
          >
            <LayoutGrid className={`h-4 w-4 ${location.pathname === MENU_OVERVIEW_PATH ? 'text-sgez-primary-dark' : 'text-slate-500'}`} />
            메뉴 전체보기
          </Link>
        </div>
      </aside>
    </>
  )
}
